export interface DeviceInfo {
  videoDevices: MediaDeviceInfo[];
  audioInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
}

/**
 * Enumerates all available media devices
 * Note: Requires getUserMedia permission first to get device labels
 */
export async function enumerateMediaDevices(): Promise<DeviceInfo> {
  try {
    // Request permissions first to get device labels
    await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

    const devices = await navigator.mediaDevices.enumerateDevices();

    const videoInputs = devices.filter(d => d.kind === 'videoinput');
    const audioInputs = devices.filter(d => d.kind === 'audioinput');
    const audioOutputs = devices.filter(d => d.kind === 'audiooutput');

    console.log('📹 Devices enumerated:', {
      video: videoInputs.length,
      audioInput: audioInputs.length,
      audioOutput: audioOutputs.length
    });

    return {
      videoDevices: videoInputs,
      audioInputDevices: audioInputs,
      audioOutputDevices: audioOutputs
    };
  } catch (error) {
    console.error('❌ Error enumerating devices:', error);
    throw error;
  }
}

/**
 * Sets default device selection if no device is currently selected
 */
export function getDefaultDeviceSelection(
  devices: DeviceInfo,
  currentVideoDevice?: string,
  currentAudioInput?: string,
  currentAudioOutput?: string
): {
  videoDevice: string;
  audioInput: string;
  audioOutput: string;
} {
  return {
    videoDevice: currentVideoDevice || (devices.videoDevices.length > 0 ? devices.videoDevices[0].deviceId : ''),
    audioInput: currentAudioInput || (devices.audioInputDevices.length > 0 ? devices.audioInputDevices[0].deviceId : ''),
    audioOutput: currentAudioOutput || (devices.audioOutputDevices.length > 0 ? devices.audioOutputDevices[0].deviceId : '')
  };
}

/**
 * Sets audio output device for a video element
 */
export async function setAudioOutputDevice(
  videoElement: HTMLVideoElement | null,
  deviceId: string
): Promise<void> {
  if (!videoElement) {
    throw new Error('Video element is null');
  }

  if ('setSinkId' in videoElement) {
    try {
      await (videoElement as any).setSinkId(deviceId);
      console.log('✅ Audio output device applied');
    } catch (error) {
      console.warn('Failed to set audio output device:', error);
      throw error;
    }
  } else {
    throw new Error('setSinkId is not supported on this element');
  }
}

/**
 * Gets device label or fallback name
 */
export function getDeviceLabel(device: MediaDeviceInfo, fallbackPrefix: string = 'Device'): string {
  return device.label || `${fallbackPrefix} ${device.deviceId.slice(0, 8)}`;
}



